/** Time helpers. Case views always show local time + UTC (04 §15). */
const pad = (n: number) => String(n).padStart(2, "0");

/**
 * The kit's default locale for dates and spoken durations (a BCP 47 tag for `Intl`). Apps pass their own, e.g. the
 * page's language as `pl-PL` (REQUESTS #141), through `KitCopyProvider` or a component's `locale` prop.
 */
export const DEFAULT_LOCALE = "en-GB";

const unitFormats = new Map<string, Intl.NumberFormat>();
/** "23 hours", "23 godziny" (or "2 d.", "3 h" narrow): a count with its unit word in the locale (`Intl`, no copy needed). */
function unit(value: number, u: "day" | "hour" | "minute", locale: string, display: "long" | "narrow" = "long"): string {
  const k = `${locale}|${u}|${display}`;
  let f = unitFormats.get(k);
  if (!f) {
    f = new Intl.NumberFormat(locale, { style: "unit", unit: u, unitDisplay: display });
    unitFormats.set(k, f);
  }
  return f.format(value);
}
/** True for English locales (and none): the kit keeps its own English words and abbreviations there. */
const isEnglish = (locale: string | undefined) => !locale || /^en\b/i.test(locale);

export interface Remaining {
  totalMs: number;
  overdue: boolean;
  hh: string;
  mm: string;
  ss: string;
  text: string;
  spoken: string;
}
/**
 * Time left until (or past) `due`. `spoken` is for screen readers, in `locale` ("23 hours 12 minutes",
 * "23 godziny 12 minut"); `text` is the visible clock (digits, and `d`/`h` for windows of two days or more: "2 d 3 h"
 * in English, the locale's narrow units elsewhere, "2 d. 3 h" in Polish).
 */
export function remaining(due: Date, now: Date, locale: string = DEFAULT_LOCALE): Remaining {
  const totalMs = due.getTime() - now.getTime();
  const abs = Math.abs(totalMs);
  const h = Math.floor(abs / 3_600_000);
  const m = Math.floor((abs % 3_600_000) / 60_000);
  const s = Math.floor((abs % 60_000) / 1000);
  return {
    totalMs,
    overdue: totalMs < 0,
    hh: pad(h),
    mm: pad(m),
    ss: pad(s),
    // Two days or more (final reports: 14 days): days and hours; a ticking clock only near the deadline.
    text:
      h >= 48
        ? isEnglish(locale)
          ? `${Math.floor(h / 24)} d ${h % 24} h`
          : `${unit(Math.floor(h / 24), "day", locale, "narrow")} ${unit(h % 24, "hour", locale, "narrow")}`
        : `${pad(h)}:${pad(m)}:${pad(s)}`,
    spoken: h >= 48 ? `${unit(Math.floor(h / 24), "day", locale)} ${unit(h % 24, "hour", locale)}` : `${unit(h, "hour", locale)} ${unit(m, "minute", locale)}`,
  };
}
export type Urgency = "normal" | "soon" | "overdue";
export const SOON_MS = 6 * 3_600_000;
export const urgency = (r: Remaining): Urgency => (r.overdue ? "overdue" : r.totalMs < SOON_MS ? "soon" : "normal");

/** Date and time in the viewer's time zone, in `locale` (default en-GB "30 Sept, 14:05"; pl-PL "30 wrz, 14:05"). */
export const fmtLocal = (d: Date, opts: Intl.DateTimeFormatOptions = {}, locale: string = DEFAULT_LOCALE) =>
  new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", ...opts }).format(d);
export const fmtUtcShort = (d: Date) => `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
export const fmtUtc = (d: Date) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
const relativeFormats = new Map<string, Intl.RelativeTimeFormat>();
/**
 * How long ago, short: "just now", "5 min ago", "3 h ago", "2 d ago". English (no locale, or an `en` one) keeps these
 * words; any other locale says it with `Intl.RelativeTimeFormat` ("5 min temu", "przed chwilą"), no copy needed.
 */
export function relative(d: Date, now: Date, locale?: string): string {
  const min = Math.round((now.getTime() - d.getTime()) / 60_000);
  const h = Math.round(min / 60);
  if (!isEnglish(locale)) {
    const tag = locale!;
    let f = relativeFormats.get(tag);
    if (!f) {
      f = new Intl.RelativeTimeFormat(tag, { numeric: "auto", style: "short" });
      relativeFormats.set(tag, f);
    }
    if (min < 1) return f.format(0, "second");
    if (min < 60) return f.format(-min, "minute");
    return h < 24 ? f.format(-h, "hour") : f.format(-Math.round(h / 24), "day");
  }
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  return h < 24 ? `${h} h ago` : `${Math.round(h / 24)} d ago`;
}
